import React from 'react';
import { KEYS } from '../../common/keyCodes';
import { maintainScrollVisibility } from './helpers';
export const useComboboxPatternKeys = (options, contentId, onSelect, isOpen, setIsOpen, comboboxRef) => {
    const delayTolerance = 500;
    const escapeFunction = CSS.escape;
    const findOptionByInput = (newInput) => (option) => option.value
        ?.toLocaleLowerCase()
        .startsWith(newInput.toLocaleLowerCase());
    const [input, setInput] = React.useState(options.filter((option) => option.value)[0]?.value[0].toLocaleLowerCase() ??
        '');
    const [matchIndex, setMatchIndex] = React.useState(0);
    const [timestamp, setTimestamp] = React.useState(0);
    const matchedOptions = React.useMemo(() => options.filter((option) => findOptionByInput(input)(option)), [input]);
    const descendant = React.useMemo(() => matchIndex >= 0 && matchedOptions.length > 0
        ? `${contentId}_option-${matchedOptions[matchIndex].id}`
        : undefined, [contentId, matchIndex, matchedOptions]);
    const isValidSelection = (option) => option.isSelectable && !option.isDisabled;
    const updateInputAndMatchIndex = (index) => {
        const newInput = options[index]?.value[0];
        const firstMatchIdx = options.findIndex((option) => findOptionByInput(newInput)(option));
        setInput(newInput.toLocaleLowerCase());
        setMatchIndex(index - firstMatchIdx);
    };
    const getFocusedOptionIndex = () => options.findIndex((option) => option.id === matchedOptions[matchIndex]?.id);
    const handleLetters = (key, currentTime) => {
        setIsOpen(true);
        if (key === input) {
            // iterate through matches
            setMatchIndex((currentIndex) => (currentIndex + 1) % matchedOptions.length);
            setTimestamp(Date.now());
            return;
        }
        // if multiple keys are typed in quick succession, search for a phrase
        if (currentTime - timestamp <= delayTolerance) {
            const newInput = input + key;
            if (options.findIndex((option) => findOptionByInput(newInput)(option)) < 0) {
                return;
            }
            setInput((currentInput) => (currentInput + key).toLocaleLowerCase());
        }
        else {
            if (options.findIndex((option) => findOptionByInput(key)(option)) < 0) {
                return;
            }
            setInput(key.toLocaleLowerCase());
        }
        setTimestamp(Date.now());
        setMatchIndex(0);
    };
    const focusFirstOption = () => {
        updateInputAndMatchIndex(0);
    };
    const focusLastOption = () => {
        updateInputAndMatchIndex(options.length - 1);
    };
    const focusNextOption = () => {
        for (let currentIndex = getFocusedOptionIndex() + 1; currentIndex < options.length; currentIndex++) {
            const nextOption = options[currentIndex];
            if (nextOption.divider) {
                continue;
            }
            updateInputAndMatchIndex(currentIndex);
            break;
        }
    };
    const focusPreviousOption = () => {
        for (let currentIndex = getFocusedOptionIndex() - 1; currentIndex >= 0; currentIndex--) {
            const previousOption = options[currentIndex];
            if (previousOption.divider) {
                continue;
            }
            updateInputAndMatchIndex(currentIndex);
            break;
        }
    };
    const focusBackTenOptions = () => {
        // move up 10 options;
        const currentIdx = getFocusedOptionIndex();
        const newIdx = Math.max(currentIdx - 10, 0);
        updateInputAndMatchIndex(newIdx);
    };
    const focusForwardTenOptions = () => {
        // move down 10 options;
        const currentIdx = getFocusedOptionIndex();
        const newIdx = Math.min(currentIdx + 10, options.length - 1);
        updateInputAndMatchIndex(newIdx);
    };
    const selectFocused = () => {
        const focusedOptionIdx = getFocusedOptionIndex();
        const focusedOption = options[focusedOptionIdx];
        if (focusedOption && isValidSelection(focusedOption)) {
            onSelect(focusedOption);
        }
    };
    const handleListboxKeydown = (event) => {
        switch (event.key) {
            case KEYS.Enter:
            case KEYS.Spacebar:
            case KEYS.Tab:
                selectFocused();
                break;
            case KEYS.Escape:
                setIsOpen(false);
                break;
            case KEYS.ArrowDown:
                event.preventDefault();
                focusNextOption();
                break;
            case KEYS.ArrowUp:
                event.preventDefault();
                if (event.altKey) {
                    setIsOpen(false);
                    selectFocused();
                }
                else {
                    focusPreviousOption();
                }
                break;
            case KEYS.Home:
                event.preventDefault();
                focusFirstOption();
                break;
            case KEYS.End:
                event.preventDefault();
                focusLastOption();
                break;
            case KEYS.PageUp:
                event.preventDefault();
                focusBackTenOptions();
                break;
            case KEYS.PageDown:
                event.preventDefault();
                focusForwardTenOptions();
                break;
        }
    };
    const handleComboboxKeydown = (event) => {
        switch (event.key) {
            case KEYS.ArrowDown:
                break;
            case KEYS.ArrowUp:
                focusFirstOption();
                break;
            case KEYS.Enter:
            case KEYS.Spacebar:
                break;
            case KEYS.Home:
                focusFirstOption();
                break;
            case KEYS.End:
                focusLastOption();
                break;
            default:
                return;
        }
        setIsOpen(true);
    };
    const handleKeydown = (event) => {
        const { key } = event;
        const currentTime = Date.now();
        if ((currentTime - timestamp <= delayTolerance && key === ' ') ||
            (key.length === 1 && key !== ' ')) {
            handleLetters(key, currentTime);
            return;
        }
        if (isOpen) {
            handleListboxKeydown(event);
        }
        else {
            handleComboboxKeydown(event);
        }
    };
    React.useEffect(() => {
        setTimeout(() => {
            // wait for the listbox to open
            if (comboboxRef.current && contentId && descendant) {
                const listBox = comboboxRef.current.querySelector('#' + escapeFunction(contentId));
                const focusedOption = comboboxRef.current.querySelector('#' + escapeFunction(descendant));
                if (focusedOption instanceof HTMLElement &&
                    listBox instanceof HTMLElement) {
                    maintainScrollVisibility(focusedOption, listBox);
                }
            }
        }, 0);
    }, [contentId, descendant, isOpen]);
    return {
        descendant,
        handleKeydown,
        matchedOptions,
        matchIndex,
        selectFocused,
    };
};
//# sourceMappingURL=useComboboxPatternKeys.js.map